// src/pages/Contact.js
import React, { useState, useRef } from 'react';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { fadeIn, slideInLeft, slideInRight } from '../styles/animations';
import emailjs from '@emailjs/browser';

const ContactPageWrapper = styled.div`
  padding-top: 80px; // Pour compenser la hauteur du header fixe
`;

const ContactHero = styled.section`
  background-color: ${props => props.theme.colors.primary};
  padding: ${props => props.theme.spacing.xl} 0;
  color: ${props => props.theme.colors.light};
  text-align: center;
`;

const HeroContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.md};
  animation: ${fadeIn} 0.8s ease-out forwards;
`;

const HeroTitle = styled.h1`
  color: ${props => props.theme.colors.light};
  margin-bottom: ${props => props.theme.spacing.md};
  font-size: ${props => props.theme.fontSizes.xxxl};
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    font-size: ${props => props.theme.fontSizes.xxl};
  }
`;

const HeroSubtitle = styled.p`
  font-size: ${props => props.theme.fontSizes.large};
  margin-bottom: 0;
  opacity: 0.9;
  color: ${props => props.theme.colors.light};
`;

const ContactContent = styled.section`
  padding: ${props => props.theme.spacing.xl} 0;
  background-color: ${props => props.theme.colors.light};
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.md};
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.xl};
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing.lg};
  }
`;

const ContactForm = styled.div`
  animation: ${slideInLeft} 0.8s ease-out forwards;
  opacity: 0;
`;

const FormTitle = styled.h2`
  margin-bottom: ${props => props.theme.spacing.md};
  font-size: ${props => props.theme.fontSizes.xl};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  margin-bottom: ${props => props.theme.spacing.xs};
  font-weight: 500;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: ${props => props.theme.borderRadius.small};
  font-size: ${props => props.theme.fontSizes.medium};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 2px ${props => props.theme.colors.primary}30;
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: ${props => props.theme.borderRadius.small};
  font-size: ${props => props.theme.fontSizes.medium};
  min-height: 150px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 2px ${props => props.theme.colors.primary}30;
  }
`;

const SubmitButton = styled.button`
  display: inline-block;
  background-color: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.light};
  font-weight: 600;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: ${props => props.theme.borderRadius.medium};
  cursor: pointer;
  transition: background-color ${props => props.theme.transitions.fast};
  margin-top: ${props => props.theme.spacing.sm};
  
  &:hover {
    background-color: ${props => props.theme.colors.primaryDark};
  }
  
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const ContactInfo = styled.div`
  animation: ${slideInRight} 0.8s ease-out forwards;
  opacity: 0;
`;

const InfoTitle = styled.h2`
  margin-bottom: ${props => props.theme.spacing.md};
  font-size: ${props => props.theme.fontSizes.xl};
`;

const InfoItem = styled.div`
  margin-bottom: ${props => props.theme.spacing.md};
`;

const InfoLabel = styled.h3`
  font-size: ${props => props.theme.fontSizes.medium};
  margin-bottom: ${props => props.theme.spacing.xs};
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
`;

const InfoText = styled.p`
  margin-bottom: 0.25rem;
`;

const InfoLink = styled.a`
  color: ${props => props.theme.colors.primary};
  text-decoration: none;
  transition: color ${props => props.theme.transitions.fast};
  
  &:hover {
    color: ${props => props.theme.colors.primaryDark};
    text-decoration: underline;
  }
`;

const MapWrapper = styled.div`
  height: 300px;
  border-radius: ${props => props.theme.borderRadius.medium};
  overflow: hidden;
  margin-top: ${props => props.theme.spacing.lg};
  border: 1px solid #ddd;
`;

const AlertWrapper = styled.div`
  margin-bottom: ${props => props.theme.spacing.md};
  padding: 1rem;
  border-radius: ${props => props.theme.borderRadius.small};
  background-color: ${props => props.success ? '#d4edda' : '#f8d7da'};
  color: ${props => props.success ? '#155724' : '#721c24'};
`;

// Composant d'alerte
const Alert = ({ type, message }) => {
  return (
    <AlertWrapper success={type === 'success'}>
      {message}
    </AlertWrapper>
  );
};

const Contact = () => {
  const form = useRef();
  const [formData, setFormData] = useState({
    user_name: '',
    user_email: '',
    user_phone: '',
    subject: '',
    message: ''
  });

  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Envoi du formulaire via EmailJS
      const result = await emailjs.sendForm(
        'service_tqiilae', // Remplacez par votre Service ID
        'template_w75owo5', // Remplacez par votre Template ID
        form.current,
        'j6fganKkXnUajGvic' // Remplacez par votre User ID (clé publique)
      );

      console.log('Email envoyé avec succès:', result.text);

      setAlert({
        type: 'success',
        message: 'Votre message a bien été envoyé. Nous vous répondrons dans les plus brefs délais.'
      });

      // Réinitialiser le formulaire
      setFormData({
        user_name: '',
        user_email: '',
        user_phone: '',
        subject: '',
        message: ''
      });

    } catch (error) {
      console.error('Erreur lors de l\'envoi du message:', error.text);
      setAlert({
        type: 'error',
        message: 'Une erreur est survenue lors de l\'envoi de votre message. Veuillez réessayer ultérieurement.'
      });
    } finally {
      setLoading(false);

      // Faire disparaître l'alerte après 5 secondes
      setTimeout(() => {
        setAlert(null);
      }, 5000);
    }
  };

  return (
    <ContactPageWrapper>
      <Helmet>
        <title>Contact - Decko | Créateur de vos parquets de jardin</title>
        <meta name="description" content="Contactez notre équipe d'experts pour tous vos projets de terrasse en bois ou parquet de jardin. Demandez un devis gratuit." />
      </Helmet>

      <ContactHero>
        <HeroContainer>
          <HeroTitle>Contactez-nous</HeroTitle>
          <HeroSubtitle>Notre équipe est à votre disposition pour répondre à toutes vos questions</HeroSubtitle>
        </HeroContainer>
      </ContactHero>

      <ContactContent>
        <ContentContainer>
          <ContactGrid>
            <ContactForm>
              <FormTitle>Envoyez-nous un message</FormTitle>

              {alert && <Alert type={alert.type} message={alert.message} />}

              <Form ref={form} onSubmit={handleSubmit}>
                <FormGroup>
                  <Label htmlFor="user_name">Nom complet *</Label>
                  <Input
                    type="text"
                    id="user_name"
                    name="user_name"
                    value={formData.user_name}
                    onChange={handleChange}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="user_email">Email *</Label>
                  <Input
                    type="email"
                    id="user_email"
                    name="user_email"
                    value={formData.user_email}
                    onChange={handleChange}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="user_phone">Téléphone</Label>
                  <Input
                    type="tel"
                    id="user_phone"
                    name="user_phone"
                    value={formData.user_phone}
                    onChange={handleChange}
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="subject">Sujet *</Label>
                  <Input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="message">Message *</Label>
                  <TextArea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                  />
                </FormGroup>

                <SubmitButton type="submit" disabled={loading}>
                  {loading ? 'Envoi en cours...' : 'Envoyer'}
                </SubmitButton>
              </Form>
            </ContactForm>

            <ContactInfo>
              <InfoTitle>Informations de contact</InfoTitle>

              <InfoItem>
                <InfoLabel>Adresse</InfoLabel>
                <InfoText>Siège: 15, rue du Moulin des Ratelles</InfoText>
                <InfoText>Atelier : 20, Rue du Patis ZA du Patis</InfoText>
                <InfoText>44690 LA HAYE FOUASSIERE</InfoText>
              </InfoItem>

              <InfoItem>
                <InfoLabel>Téléphone</InfoLabel>
                <InfoText><InfoLink href="tel:+33240133097">02 40 13 30 97</InfoLink></InfoText>
                <InfoText><InfoLink href="tel:+33683589905">06 83 58 99 05</InfoLink></InfoText>
              </InfoItem>

              <InfoItem>
                <InfoLabel>Horaires d'ouverture</InfoLabel>
                <InfoText>Lundi - Vendredi : 8h00 - 18h00</InfoText>
                <InfoText>Samedi - Dimanche : Fermé</InfoText>
              </InfoItem>

              <MapWrapper>
                {/* Carte Google Maps mise à jour avec l'adresse du siège */}
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2710.2344695218674!2d-1.3896559339525354!3d47.20301107916147!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4805e80de92d0e53%3A0x2814f7a79c49c33b!2s15%20Rue%20du%20Moulin%20des%20Ratelles%2C%2044690%20La%20Haye-Foussière!5e0!3m2!1sfr!2sfr!4v1710869114121!5m2!1sfr!2sfr"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  title="Localisation Decko - Siège"
                ></iframe>
              </MapWrapper>
            </ContactInfo>
          </ContactGrid>
        </ContentContainer>
      </ContactContent>
    </ContactPageWrapper>
  );
};

export default Contact;